import { Link } from "react-router-dom";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendUp } from "@phosphor-icons/react";
import { Panel } from "@/components/common";
import { api, money } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

export function CashflowChart({ data, height = 280 }) {
  const rows = data.buckets.map((b) => ({ ...b }));
  return (
    <div style={{ height }} className="p-4">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ left: 8, right: 8 }}>
          <CartesianGrid stroke="#e4e4e7" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}B` : v)} />
          <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="inflow" name="Beklenen Tahsilat" fill="#047857" radius={[2, 2, 0, 0]} />
          <Bar dataKey="outflow" name="Beklenen Ödeme" fill="#dc2626" radius={[2, 2, 0, 0]} />
          <Line dataKey="cumulative" name="Tahmini Kasa" stroke="#1d4ed8" strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CashflowStats({ data }) {
  const items = [
    ["Açılış Kasa", data.opening_cash, "text-zinc-800"],
    ["90 Gün Sonu Tahmini Kasa", data.closing_cash, data.closing_cash < 0 ? "text-red-700" : "text-emerald-700"],
    ["Açık Alacak", data.open_receivables, "text-emerald-700"],
    ["Açık Borç", data.open_payables, "text-red-700"],
  ];
  return (
    <div className="grid grid-cols-2 gap-px border-b border-zinc-200 bg-zinc-200 md:grid-cols-4" data-testid="cashflow-stats">
      {items.map(([l, v, c]) => (
        <div key={l} className="bg-white px-4 py-3">
          <div className="label-eyebrow">{l}</div>
          <div className={`font-num text-lg font-bold ${c}`}>{money(v)}</div>
        </div>
      ))}
      <div className="col-span-2 bg-white px-4 py-2 text-xs text-muted-foreground md:col-span-4" data-testid="cashflow-assumptions">
        Varsayım: son 90 günde tahsilat oranı <b>%{Math.round(data.collection_rate * 100)}</b>, ortalama gecikme <b>{data.avg_delay_days} gün</b>; vadesi geçen {money(data.overdue_receivables)} alacak ilk 30 günde tahsil edilir kabul edildi. Portföydeki çek/senetler dahildir.
      </div>
    </div>
  );
}

export default function CashflowCard() {
  const [d] = useLoad(() => api.get("/reports/cashflow-forecast").then((r) => r.data), []);
  if (!d) return null;
  return (
    <Panel title="Nakit Akışı Tahmini (90 gün)" icon={TrendUp} accent="green" testid="dashboard-cashflow"
      right={<Link to="/raporlar?tip=cashflow" className="text-xs font-semibold text-teal-700 hover:underline" data-testid="dashboard-cashflow-link">Detaylı rapor →</Link>}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        <CashflowChart data={d} height={240} />
        <div className="divide-y border-l border-zinc-200 text-sm">
          {d.buckets.map((b) => (
            <div key={b.key} className="flex items-center justify-between px-4 py-3" data-testid={`cashflow-bucket-${b.key}`}>
              <span className="text-muted-foreground">{b.label}</span>
              <span className={`font-num font-semibold ${b.net < 0 ? "text-red-700" : "text-emerald-700"}`}>{b.net >= 0 ? "+" : ""}{money(b.net)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="font-semibold">Tahmini kasa</span>
            <span className={`font-num font-bold ${d.closing_cash < 0 ? "text-red-700" : "text-blue-700"}`} data-testid="cashflow-closing">{money(d.closing_cash)}</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}
